import { CognitoAuth } from "amazon-cognito-auth-js";
import { authConfig } from "./config";
import { getEnvironment } from "../environmentInfo/EnvironmentInfo";
import { MetricsPublisher } from "../metrics/metrics";
import { isSaamTestUser } from "./saamAuthorization";

function getAuthConfigFromEnvironment() {
  return authConfig[getEnvironment() as keyof typeof authConfig];
}

// This code removes the "?code=..." from the URL. It is because the grant code is not reusable.
//  Sometimes the SDK will report weird message because of using old grant code.
function removeQueryFromLocation() {
  // Replace the href because the Cognito passes the OAuth2 grant code in the query string
  // And the grant code is not reusable
  if (window.history.length > 0) {
    let newHref = window.location.href.split("?")[0];

    const params = new URLSearchParams(window.location.href.split("?")[1]);
    params.delete("code");
    params.delete("state");

    //Add rest of the query params if they exist
    newHref = params.entries().next().value ? `${newHref}?${params.toString()}` : newHref;

    window.history.replaceState(undefined, "", newHref);
  }
}

/**
 * Gets a cookie value by name
 */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Checks if SAAM authentication is present by looking for Midway cookies
 * The cypress-midway-plugin sets a 'user_name' cookie with the SAAM username
 */
function detectSaamAuthentication(): string | null {
  const userName = getCookie("user_name");
  if (userName && isSaamTestUser(userName)) {
    return userName;
  }
  return null;
}

/**
 * Creates a mock CognitoAuth-like object for SAAM authentication
 * This allows SAAM users to bypass Cognito while maintaining compatibility
 * Uses the real Cognito JWT token
 */
function createSaamAuthObject(saamUsername: string): CognitoAuth {
  // Get the Cognito JWT token from the window (set by Cypress tests)
  const cognitoJwtToken = typeof window !== "undefined" ? window.__COGNITO_JWT_TOKEN__ : null;

  const mockSession = {
    isValid: () => true,
    getIdToken: () => ({
      decodePayload: () => ({
        "cognito:username": `Federate_${saamUsername}`,
        "custom:alias": saamUsername,
        given_name: "SAAM",
        family_name: "Test User",
        email: `${saamUsername}@amazon.com`,
        "custom:location": "TEST",
        "custom:manager": "(test-manager)",
        "custom:jobDescription": "Automated UI Testing Account",
        profile: "[]", // Roles are handled by createSaamTestUser
      }),
      // Return the real Cognito JWT token from Cypress
      getJwtToken: () => cognitoJwtToken || "saam-mock-token",
    }),
    getAccessToken: () => ({
      getJwtToken: () => cognitoJwtToken || "saam-mock-access-token",
    }),
    getRefreshToken: () => ({
      getToken: () => "saam-mock-refresh-token",
    }),
  };

  // Create a minimal CognitoAuth-like object
  const mockAuth = {
    getSignInUserSession: () => mockSession,
    signOut: () => {
      // No-op for SAAM auth - session managed by Midway
    },
  } as unknown as CognitoAuth;

  return mockAuth;
}

/**
 * Ensures the user is authenticated.
 * If authenticated, return auth object. Otherwise force authentication.
 * Auth object will be used to call API gateway
 *
 * SAAM Support: If SAAM authentication is detected (via Midway cookies),
 * bypasses Cognito and returns a mock auth object for the SAAM test user.
 */
export function ensureAuthenticated(): Promise<CognitoAuth> {
  return new Promise((resolve) => {
    // Check for SAAM authentication first
    const saamUsername = detectSaamAuthentication();
    if (saamUsername) {
      const metricsPublisher = new MetricsPublisher("api.authenticate");
      metricsPublisher.publishAuthenticationTokenValidMetrics();
      resolve(createSaamAuthObject(saamUsername));
      return;
    }

    // Normal Cognito authentication flow
    const auth = new CognitoAuth(getAuthConfigFromEnvironment());
    const metricsPublisher = new MetricsPublisher("api.authenticate");
    auth.useCodeGrantFlow();

    // Register callback functions
    auth.userhandler = {
      onFailure: () => {
        removeQueryFromLocation();
        metricsPublisher.publishAuthenticationTokenInvalidMetrics();
        /* Log in may fail if an authorized user's refresh token has expired.
         * Clear the cache will redirect them to midway page for authentication and acquire a new refresh token
         * Yet this may result in infinite loop for unauthorized user.
         * May need to change this to sign out button in the future.
         */
        auth.clearCachedTokensScopes();
        window.location.reload();
      },
      onSuccess: () => {
        // We only need to remove the query string if the original href saved before
        // getting the session is not present.

        if (!sessionStorage.getItem("hrefBeforeGetSession")) {
          removeQueryFromLocation();
        }
        metricsPublisher.publishAuthenticationTokenValidMetrics();
        //Getting tokens here as well to get them on first load
        resolve(auth);
      },
    };

    const href = window.location.href;
    const session = auth.getSignInUserSession();

    if (session.isValid()) {
      resolve(auth);
    } else if (href.indexOf("?code") > 0) {
      // This is required because Cognito needs to get the authentication result from the query string
      // The parsing is done asynchronously, and the result will be passed to the userHandler.
      // Once the result is parsed, onSuccess userhandler will resolve the promise.
      auth.parseCognitoWebResponse(href);
    } else {
      // Save href before getting the session to redirect in case we are not coming from the base URL
      // Using session storage to avoid redirecting to root when opening new tabs as session storage is
      // not shared between tabs
      sessionStorage.setItem("hrefBeforeGetSession", window.location.href);

      // Cognito SDK will handle session refresh / authentication.
      auth.getSession();
    }
  });
}
